import { cn } from "@/lib/utils";

interface Integration {
  name: string;
  description: string;
  badge: string;
  category: "Language Models" | "Image Generation" | "Video & Audio";
  icon: React.ReactNode;
}

function OpenAILogo() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="size-6">
      <path d="M22.28 9.82a5.98 5.98 0 0 0-.52-4.91 6.05 6.05 0 0 0-6.51-2.9A6.07 6.07 0 0 0 4.98 4.18a5.98 5.98 0 0 0-4 2.9 6.05 6.05 0 0 0 .74 7.1 5.98 5.98 0 0 0 .51 4.91 6.05 6.05 0 0 0 6.52 2.9A5.98 5.98 0 0 0 13.26 24a6.06 6.06 0 0 0 5.77-4.21 5.99 5.99 0 0 0 4-2.9 6.06 6.06 0 0 0-.75-7.07zm-9.02 12.61a4.48 4.48 0 0 1-2.88-1.04l.14-.08 4.78-2.76a.79.79 0 0 0 .39-.68v-6.74l2.02 1.17a.07.07 0 0 1 .04.05v5.58a4.5 4.5 0 0 1-4.49 4.5zm-9.66-4.13a4.47 4.47 0 0 1-.53-3.01l.14.09 4.78 2.76a.77.77 0 0 0 .78 0l5.84-3.37v2.33a.08.08 0 0 1-.03.06l-4.84 2.79a4.5 4.5 0 0 1-6.14-1.65zM2.34 7.9a4.49 4.49 0 0 1 2.37-1.97V11.6a.77.77 0 0 0 .39.68l5.81 3.35-2.02 1.17a.08.08 0 0 1-.07 0L4.02 14.02A4.5 4.5 0 0 1 2.34 7.87zm16.6 3.86L13.1 8.36l2.02-1.16a.08.08 0 0 1 .07 0l4.83 2.79a4.49 4.49 0 0 1-.68 8.1V12.44a.79.79 0 0 0-.4-.68zm2.01-3.02l-.14-.09-4.77-2.78a.78.78 0 0 0-.79 0L9.41 9.23V6.9a.07.07 0 0 1 .03-.06l4.83-2.79a4.5 4.5 0 0 1 6.68 4.66zM8.31 12.86l-2.02-1.16a.08.08 0 0 1-.04-.06V6.07a4.5 4.5 0 0 1 7.38-3.45l-.14.08-4.78 2.76a.79.79 0 0 0-.39.68zm1.1-2.37l2.6-1.5 2.6 1.5v3l-2.6 1.5-2.6-1.5z" />
    </svg>
  );
}

function GeminiLogo() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="size-6">
      <path d="M12 0c0 6.627-5.373 12-12 12 6.627 0 12 5.373 12 12 0-6.627 5.373-12 12-12-6.627 0-12-5.373-12-12Z" />
    </svg>
  );
}

function AnthropicLogo() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="size-6">
      <path d="M13.827 3.52h3.603L24 20h-3.603l-6.57-16.48zm-7.258 0h3.767L16.906 20h-3.674l-1.343-3.461H5.017L3.674 20H0L6.57 3.52zm4.132 9.959L8.453 7.687 6.205 13.48h4.496z" />
    </svg>
  );
}

function ElevenLabsLogo() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="size-6">
      <rect x="7" y="3" width="3.5" height="18" rx="1" />
      <rect x="13.5" y="3" width="3.5" height="18" rx="1" />
    </svg>
  );
}

function TextLogo({ children }: { children: string }) {
  return (
    <span className="text-foreground text-sm font-bold tracking-tight">
      {children}
    </span>
  );
}

const integrations: Integration[] = [
  {
    name: "OpenAI",
    description:
      "GPT-5.4, o3, and o4-mini for advanced reasoning and general tasks.",
    badge: "Text",
    category: "Language Models",
    icon: <OpenAILogo />,
  },
  {
    name: "Google Gemini",
    description:
      "Gemini 3.1 Pro, Gemini 3 Flash, and multimodal understanding models.",
    badge: "Text",
    category: "Language Models",
    icon: <GeminiLogo />,
  },
  {
    name: "Anthropic",
    description:
      "Claude Opus 4.6 and Sonnet 4.6 for complex reasoning and long-horizon tasks.",
    badge: "Text",
    category: "Language Models",
    icon: <AnthropicLogo />,
  },
  {
    name: "Nano Banana 2",
    description: "Nano Banana 2 - Google's latest image generation model.",
    badge: "Image",
    category: "Image Generation",
    icon: <GeminiLogo />,
  },
  {
    name: "FLUX",
    description:
      "FLUX.2 Pro and FLUX.1 Kontext for photorealistic image generation and editing.",
    badge: "Image",
    category: "Image Generation",
    icon: <TextLogo>FLUX</TextLogo>,
  },
  {
    name: "GPT Image 1.5",
    description: "OpenAI's image generation model for photorealistic output.",
    badge: "Image",
    category: "Image Generation",
    icon: <OpenAILogo />,
  },

  {
    name: "ElevenLabs",
    description:
      "Eleven v3 — ultra-realistic TTS with 70+ languages and multi-speaker dialogue.",
    badge: "Audio",
    category: "Video & Audio",
    icon: <ElevenLabsLogo />,
  },
  {
    name: "Veo 3.1",
    description: "Veo 3.1 — Google's latest video generation model.",
    badge: "Video",
    category: "Video & Audio",
    icon: <GeminiLogo />,
  },
  {
    name: "KlingAI",
    description:
      "Kling V3 — cinematic video sequences with advanced physics and motion control.",
    badge: "Video",
    category: "Video & Audio",
    icon: <TextLogo>KL</TextLogo>,
  },
];

const badgeColors: Record<string, string> = {
  Text: "bg-blue-500/10 text-blue-400",
  Image: "bg-violet-500/10 text-violet-400",
  Video: "bg-rose-500/10 text-rose-400",
  Audio: "bg-amber-500/10 text-amber-400",
};

const categories = [
  "Language Models",
  "Image Generation",
  "Video & Audio",
] as const;

export function IntegrationsGrid() {
  return (
    <section className="pb-24 pt-8">
      <div className="mx-auto max-w-5xl px-6">
        {categories.map((category) => (
          <div key={category} className="mb-16">
            <h2 className="text-foreground mb-6 text-sm font-semibold uppercase tracking-widest">
              {category}
            </h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              {integrations
                .filter((i) => i.category === category)
                .map((integration) => (
                  <div
                    key={integration.name}
                    className="flex flex-col rounded-2xl border border-black/5 bg-card p-6 shadow-sm dark:border-white/10 dark:bg-white/5"
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-foreground flex size-10 items-center justify-center rounded-lg bg-muted">
                        {integration.icon}
                      </div>
                      <span
                        className={cn(
                          "rounded-full px-2.5 py-0.5 text-xs font-medium",
                          badgeColors[integration.badge],
                        )}
                      >
                        {integration.badge}
                      </span>
                    </div>
                    <h3 className="text-foreground mt-4 text-base font-semibold">
                      {integration.name}
                    </h3>
                    <p className="text-muted-foreground mt-1.5 text-sm leading-relaxed">
                      {integration.description}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
